/**
 * Who is signed in, and which role they hold.
 *
 * The role is the user's role at their firm (MFD, PORTFOLIO_MANAGER, CIO,
 * PMS_ADVISOR). The backend puts it on the JWT as the `role` claim once the
 * firm membership is APPROVED — a founder at once, anyone else when the owner
 * approves — so resolution is a single seam with a clear precedence:
 *
 *   1. a Keep Investing staff claim on the JWT — the console seat
 *   2. the `role` claim on the JWT — the backend's word
 *   3. the role chosen at signup, recorded per user id on this device, which
 *      only matters until the firm is set up (or on an old token)
 *   4. nothing, in which case the app stays in the design's test-profile mode
 */
import { SEAT_KEYS } from '../config/navigation';

const STORE = 'ki.userRole';


/**
 * Map whatever a token or a form gives us onto our seats.
 *
 * `user_type` is a LIST on the backend's token, and a Keep Investing employee
 * carries ADMIN or EMPLOYEE in it — which is exactly the claim the backend's
 * own `is_employee_context` gate reads before letting a query answer for
 * somebody else's client. So ADMIN and EMPLOYEE resolve to the staff seat.
 */
export function normaliseRole(value) {
  if (!value) return null;
  const raw = Array.isArray(value) ? value : [value];
  for (const entry of raw) {
    const v = String(entry).trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (SEAT_KEYS.includes(v)) return v;
    if (v === 'admin' || v === 'super_admin' || v === 'superuser' || v === 'staff') return 'employee';
    if (v === 'pms_advisor' || v === 'pms_rm' || v === 'pms/rm' || v === 'rm') return 'pms';
    if (v === 'fund_manager' || v === 'portfolio_manager') return 'pm';
    if (v === 'distributor' || v === 'arn') return 'mfd';
    if (v === 'chief_investment_officer') return 'cio';
    if (v === 'investor' || v === 'retail' || v === 'self_directed' || v === 'individual_investor') return 'individual';
  }
  return null;
}

/** Decode a JWT payload without verifying it — the server does that. */
export function decodeToken(token) {
  if (!token || typeof token !== 'string' || token.split('.').length < 2) return null;
  try {
    const body = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(body))));
  } catch {
    return null;
  }
}

const readStore = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE) || '{}');
  } catch {
    return {};
  }
};

/** Record the role a user picked at signup, against their user id. */
export function setRoleForUser(userId, role) {
  const key = normaliseRole(role);
  if (!userId || !key) return;
  try {
    localStorage.setItem(STORE, JSON.stringify({ ...readStore(), [String(userId)]: key }));
  } catch {
    /* storage unavailable — the role just won't survive a reload */
  }
}

export function roleForUser(userId) {
  if (!userId) return null;
  return normaliseRole(readStore()[String(userId)]);
}

/**
 * The role this session should run as, and whether it is fixed.
 *
 * `assigned` non-null means the account has a role, so the app shows only that
 * role's screens and the profile switcher locks to it.
 */
export function resolveSessionRole({ token, userId } = {}) {
  const claims = decodeToken(token) || {};
  /* Staff outranks the firm role. An employee may also hold an advisory role
     at a firm, but the account they signed in with is KI's, and the console is
     the seat that token unlocks. */
  const staff = isEmployeeToken(token) ? 'employee' : null;
  const fromToken = staff || normaliseRole(claims.role ?? claims.user_role ?? claims.user_type);
  const fromSignup = roleForUser(userId ?? claims.sub ?? claims.id);
  const assigned = fromToken || fromSignup || null;
  return { assigned, source: fromToken ? 'token' : fromSignup ? 'signup' : null };
}

/**
 * Does this token belong to a Keep Investing employee?
 *
 * The same three things the backend's `is_employee_context` accepts: ADMIN or
 * EMPLOYEE among the token's `user_type` list, a super-admin flag, or an
 * explicit `is_employee`. Read here so the app offers the console on exactly
 * the accounts the server will actually answer staff queries for — anything
 * else would produce a seat whose every screen is refused.
 */
export function isEmployeeToken(token) {
  const claims = decodeToken(token);
  if (!claims) return false;
  if (claims.is_employee || claims.isEmployee) return true;
  if (claims.is_super_user || claims.is_superuser) return true;
  const types = claims.user_type ?? claims.userType ?? [];
  const list = Array.isArray(types) ? types : [types];
  return list.some((t) => ['ADMIN', 'EMPLOYEE'].includes(String(t).trim().toUpperCase()));
}
